'use client'

import { useCallback, useMemo } from 'react'
import { useLandingAuthContext } from '@/features/landing-page/landing-auth-context'
import { cn } from '@/utils/styles/cn'
import { OnboardingSection } from '@/features/landing-page/onboarding-section'
import { OnboardingSectionLogoBar } from '@/features/landing-page/onboarding-section-logo-bar'
import type { ILandingRouteContentLogo } from '@/features/landing-page/helpers/landing-route-content'

interface ILandingPageContentClientProps {
    titleText: string
    subtitleText: string
    theme?: 'default' | 'light'
    titleClassName?: string
    gradientSrc?: string
    ctaLabel?: string
    ctaHref?: string
    secondaryCtaLabel?: string
    secondaryCtaHref?: string
    bottomLogos?: ILandingRouteContentLogo[]
    bottomLogoLabel?: string
    bottomTagline?: string
}

export const LandingPageContentClient = ({
    titleText,
    subtitleText,
    theme = 'default',
    titleClassName,
    gradientSrc,
    ctaLabel,
    ctaHref,
    secondaryCtaLabel,
    secondaryCtaHref,
    bottomLogos,
    bottomLogoLabel,
    bottomTagline,
}: ILandingPageContentClientProps) => {
    const { handleCtaClick } = useLandingAuthContext()

    const handleClick = useCallback(() => {
        if (ctaHref) {
            window.open(ctaHref, '_blank', 'noopener,noreferrer')
        } else {
            handleCtaClick()
        }
    }, [ctaHref, handleCtaClick])

    const handleSecondaryClick = useCallback(() => {
        if (secondaryCtaHref) {
            window.open(secondaryCtaHref, '_blank', 'noopener,noreferrer')
        }
    }, [secondaryCtaHref])

    const bottomContent = useMemo(() => {
        if (bottomLogos?.length) return <OnboardingSectionLogoBar logos={bottomLogos} label={bottomLogoLabel} />
        if (bottomTagline) return bottomTagline
        return undefined
    }, [bottomLogos, bottomLogoLabel, bottomTagline])

    return (
        <OnboardingSection
            titleText={titleText}
            subtitleText={subtitleText}
            onCtaClick={handleClick}
            theme={theme}
            className={cn(titleClassName)}
            gradientSrc={gradientSrc}
            ctaLabel={ctaLabel}
            secondaryCtaLabel={secondaryCtaLabel}
            onSecondaryCtaClick={secondaryCtaHref ? handleSecondaryClick : undefined}
            bottomContent={bottomContent}
        />
    )
}
